/**
 * Cross-tab leader election (REALTIME.md §5.1).
 *
 * The tab holding the Web Lock `tik-sse-leader` opens the only EventSource and re-broadcasts
 * every frame on `BroadcastChannel('tik-events')`; followers apply frames exactly as if they
 * came from the network. Every tab keeps a pending lock request, so when the leader closes the
 * lock passes on and the next tab takes over (it carries `Last-Event-ID` from IndexedDB).
 *
 * Without Web Locks every tab runs `solo` (its own stream). A follower that becomes visible after
 * 40 s without any leader message treats the leader as frozen and asks to open its own stream
 * (`onStale`) until a leader message arrives again.
 */

export const LEADER_LOCK = 'tik-sse-leader';
export const EVENTS_CHANNEL = 'tik-events';
export const LEADER_STALE_MS = 40_000;
export const LEADER_PING_MS = 15_000;

export type LeaderRole = 'leader' | 'follower' | 'solo' | 'idle';

/** A raw SSE frame as forwarded between tabs. */
export interface SseFrame {
  id: number;
  event: string;
  data: string;
}

export type ChannelMessage =
  | { type: 'leader'; id: string }
  | { type: 'ping'; id: string }
  | { type: 'frame'; id: string; frame: SseFrame }
  | { type: 'state'; id: string; state: string };

/** Minimal `BroadcastChannel` surface (injectable for tests). */
export interface ChannelLike {
  postMessage(msg: unknown): void;
  close(): void;
  onmessage: ((ev: { data: unknown }) => void) | null;
}

/** Minimal `LockManager` surface (injectable for tests). */
export interface LocksLike {
  request(name: string, options: { mode: 'exclusive'; signal?: AbortSignal }, callback: () => Promise<void>): Promise<void>;
}

export interface LeaderOptions {
  lockName?: string;
  channelName?: string;
  locks?: LocksLike | null;
  channelFactory?: (name: string) => ChannelLike | null;
  staleMs?: number;
  pingMs?: number;
  now?: () => number;
  /** Document for visibility tracking (null in tests without DOM). */
  doc?: Document | null;
}

function randomId(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

export class LeaderElector {
  readonly id = randomId();
  role: LeaderRole = 'idle';
  /** Last time any message from the leader was seen (followers). */
  lastLeaderPing = 0;
  private readonly lockName: string;
  private readonly locks: LocksLike | null;
  private readonly staleMs: number;
  private readonly pingMs: number;
  private readonly now: () => number;
  private readonly doc: Document | null;
  private channel: ChannelLike | null = null;
  private release: (() => void) | null = null;
  private abort: AbortController | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private roleListeners = new Set<(role: LeaderRole) => void>();
  private frameListeners = new Set<(frame: SseFrame) => void>();
  private stateListeners = new Set<(state: string) => void>();
  private staleListeners = new Set<() => void>();
  private readonly onVisibility = () => this.checkStale();

  constructor(opts: LeaderOptions = {}) {
    this.lockName = opts.lockName ?? LEADER_LOCK;
    this.staleMs = opts.staleMs ?? LEADER_STALE_MS;
    this.pingMs = opts.pingMs ?? LEADER_PING_MS;
    this.now = opts.now ?? Date.now;
    this.doc = opts.doc === undefined ? (typeof document !== 'undefined' ? document : null) : opts.doc;
    this.locks = opts.locks === undefined ? (typeof navigator !== 'undefined' && navigator.locks ? (navigator.locks as unknown as LocksLike) : null) : opts.locks;
    const factory =
      opts.channelFactory ??
      ((name: string) => (typeof BroadcastChannel !== 'undefined' ? (new BroadcastChannel(name) as unknown as ChannelLike) : null));
    this.channel = factory(opts.channelName ?? EVENTS_CHANNEL);
    if (this.channel) this.channel.onmessage = (ev) => this.onChannel(ev.data as ChannelMessage);
  }

  onRole(cb: (role: LeaderRole) => void): () => void {
    this.roleListeners.add(cb);
    return () => this.roleListeners.delete(cb);
  }
  /** Frames forwarded by the leader (followers only). */
  onFrame(cb: (frame: SseFrame) => void): () => void {
    this.frameListeners.add(cb);
    return () => this.frameListeners.delete(cb);
  }
  /** Connection state mirrored from the leader (followers only). */
  onLeaderState(cb: (state: string) => void): () => void {
    this.stateListeners.add(cb);
    return () => this.stateListeners.delete(cb);
  }
  /** Fired when a visible follower has heard nothing from the leader for `staleMs`. */
  onStale(cb: () => void): () => void {
    this.staleListeners.add(cb);
    return () => this.staleListeners.delete(cb);
  }

  /** Begin: solo without locks, otherwise queue for the lock (leader when granted). */
  start(): void {
    if (this.role !== 'idle') return;
    this.doc?.addEventListener('visibilitychange', this.onVisibility);
    if (!this.locks) {
      this.setRole('solo');
      return;
    }
    this.lastLeaderPing = this.now();
    this.setRole('follower');
    this.abort = new AbortController();
    this.locks
      .request(this.lockName, { mode: 'exclusive', signal: this.abort.signal }, () => {
        return new Promise<void>((resolve) => {
          this.release = resolve;
          this.becomeLeader();
        });
      })
      .catch(() => {
        /* aborted by stop(), or locks unavailable at runtime → fall back to solo */
        if (this.role === 'follower' && !this.abort?.signal.aborted) this.setRole('solo');
      });
  }

  /** Release the lock (if leader), cancel the pending request, close the channel. */
  stop(): void {
    this.doc?.removeEventListener('visibilitychange', this.onVisibility);
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
    this.abort?.abort();
    this.abort = null;
    this.release?.();
    this.release = null;
    this.channel?.close();
    this.channel = null;
    this.setRole('idle');
  }

  get isLeader(): boolean {
    return this.role === 'leader';
  }

  /** Owns the network stream: leader or solo. */
  get ownsStream(): boolean {
    return this.role === 'leader' || this.role === 'solo';
  }

  /** Leader → followers. */
  broadcastFrame(frame: SseFrame): void {
    if (this.role !== 'leader') return;
    this.post({ type: 'frame', id: this.id, frame });
  }

  broadcastState(state: string): void {
    if (this.role !== 'leader') return;
    this.post({ type: 'state', id: this.id, state });
  }

  /** Follower staleness check (also run on `visibilitychange`). */
  checkStale(): boolean {
    if (this.role !== 'follower') return false;
    if (this.doc && this.doc.visibilityState !== 'visible') return false;
    const stale = this.now() - this.lastLeaderPing > this.staleMs;
    if (stale) for (const cb of this.staleListeners) cb();
    return stale;
  }

  /** Simulate an incoming channel message (tests, or a custom transport). */
  onChannel(msg: ChannelMessage): void {
    if (!msg || typeof msg !== 'object' || msg.id === this.id) return;
    if (this.role === 'leader') return; // a second leader cannot exist; ignore echoes
    this.lastLeaderPing = this.now();
    if (msg.type === 'frame') for (const cb of this.frameListeners) cb(msg.frame);
    else if (msg.type === 'state') for (const cb of this.stateListeners) cb(msg.state);
  }

  private becomeLeader(): void {
    this.setRole('leader');
    this.post({ type: 'leader', id: this.id });
    this.pingTimer = setInterval(() => this.post({ type: 'ping', id: this.id }), this.pingMs);
  }

  private post(msg: ChannelMessage): void {
    try {
      this.channel?.postMessage(msg);
    } catch {
      /* channel closed */
    }
  }

  private setRole(role: LeaderRole): void {
    if (this.role === role) return;
    this.role = role;
    for (const cb of this.roleListeners) cb(role);
  }
}
